import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { userAPI, componentsAPI, achievementsAPI, workshopsAPI, authAPI, subscriptionAPI } from '../services/api';

/**
 * Global state management for user data, inventory, and progress
 */
const useUserStore = create(
  persist(
    (set, get) => ({
      // User data
      user: null,
      token: null,
      isAuthenticated: false,
      emailVerified: false,

      // User stats
      stats: {
        challenges: { completed: 0, unlocked: 0, attempted: 0 },
        submissions: { total: 0, avg_score: 0, best_score: 0 },
        components: { unlocked: 0, total: 0 },
      },

      // Component inventory
      inventory: [],
      inventoryLoaded: false,

      // Achievements
      achievements: [],
      achievementsLoaded: false,

      // Streak
      streak: null,
      streakLoaded: false,

      // Workshops
      workshops: [],
      workshopsLoaded: false,

      // Subscription
      subscriptionStatus: null,
      subscriptionLoaded: false,

      // Actions
      setUser: (user, token, inventory = null) => {
        const stateUpdate = {
          user,
          token,
          isAuthenticated: true,
          emailVerified: user?.email_verified || false,
        };

        // If inventory is provided, set it too
        if (inventory) {
          stateUpdate.inventory = inventory;
          stateUpdate.inventoryLoaded = true;
        }

        set(stateUpdate);

        if (token) {
          localStorage.setItem('token', token);
        }
      },

      setEmailVerified: (verified) => {
        set({ emailVerified: verified });
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, email_verified: verified } });
        }
      },

      logout: async () => {
        // Call backend to revoke token (fire and forget - clear local state regardless)
        try {
          await authAPI.logout();
        } catch (error) {
          console.log('Logout API call failed:', error);
        }

        localStorage.removeItem('token');
        localStorage.removeItem('user');
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          emailVerified: false,
          inventory: [],
          inventoryLoaded: false,
          achievements: [],
          achievementsLoaded: false,
          streak: null,
          streakLoaded: false,
          workshops: [],
          workshopsLoaded: false,
        });
      },

      updateDesignPoints: (points) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: {
              ...currentUser,
              design_points: points,
            },
          });
        }
      },

      fetchProfile: async () => {
        try {
          const response = await userAPI.getProfile();
          const { user, stats } = response.data;
          set({
            user,
            stats,
          });
          return response.data;
        } catch (error) {
          console.error('Error fetching profile:', error);
          throw error;
        }
      },

      fetchInventory: async () => {
        try {
          const response = await componentsAPI.getInventory();
          set({
            inventory: response.data.inventory,
            inventoryLoaded: true,
          });
          return response.data.inventory;
        } catch (error) {
          console.error('Error fetching inventory:', error);
          throw error;
        }
      },

      addToInventory: (component) => {
        const currentInventory = get().inventory;
        set({
          inventory: [...currentInventory, component],
        });
      },

      hasComponent: (componentId) => {
        const inventory = get().inventory;
        return inventory.some((c) => c.id === componentId);
      },

      canAfford: (cost) => {
        const user = get().user;
        return user && user.design_points >= cost;
      },

      // Achievement actions
      fetchAchievements: async () => {
        try {
          const response = await achievementsAPI.getAll();
          set({
            achievements: response.data.achievements || [],
            achievementsLoaded: true,
          });
          return response.data.achievements;
        } catch (error) {
          console.error('Error fetching achievements:', error);
          throw error;
        }
      },

      claimAchievement: async (achievementId) => {
        try {
          const response = await achievementsAPI.claim(achievementId);
          // Update design points
          if (response.data.new_design_points !== undefined) {
            set({
              user: {
                ...get().user,
                design_points: response.data.new_design_points,
              },
            });
          }
          // Refresh achievements
          await get().fetchAchievements();
          return response.data;
        } catch (error) {
          console.error('Error claiming achievement:', error);
          throw error;
        }
      },

      // Streak actions
      fetchStreak: async () => {
        try {
          const response = await userAPI.getStreak();
          set({
            streak: response.data,
            streakLoaded: true,
          });
          return response.data;
        } catch (error) {
          console.error('Error fetching streak:', error);
          throw error;
        }
      },

      checkInStreak: async () => {
        try {
          const response = await userAPI.checkInStreak();
          // Update streak data
          set({
            streak: {
              ...get().streak,
              streak: {
                ...get().streak?.streak,
                current_streak: response.data.current_streak,
                longest_streak: response.data.longest_streak,
              },
              daily_bonus: response.data.daily_bonus,
              can_check_in: false,
            },
          });
          return response.data;
        } catch (error) {
          console.error('Error checking in streak:', error);
          throw error;
        }
      },

      // Workshop actions
      fetchWorkshops: async () => {
        try {
          const response = await workshopsAPI.getAll();
          set({
            workshops: response.data.workshops || [],
            workshopsLoaded: true,
          });
          return response.data.workshops;
        } catch (error) {
          console.error('Error fetching workshops:', error);
          throw error;
        }
      },

      // Subscription actions
      fetchSubscriptionStatus: async () => {
        try {
          const response = await subscriptionAPI.getStatus();
          set({
            subscriptionStatus: response.data,
            subscriptionLoaded: true,
          });
          // Update user's premium status
          const currentUser = get().user;
          if (currentUser) {
            set({
              user: {
                ...currentUser,
                is_premium: response.data.is_premium,
                premium_expires_at: response.data.expires_at,
              },
            });
          }
          return response.data;
        } catch (error) {
          console.error('Error fetching subscription status:', error);
          throw error;
        }
      },

      setPremiumStatus: (isPremium, expiresAt) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: {
              ...currentUser,
              is_premium: isPremium,
              premium_expires_at: expiresAt,
            },
            subscriptionStatus: {
              ...get().subscriptionStatus,
              is_premium: isPremium,
              expires_at: expiresAt,
            },
          });
        }
      },
    }),
    {
      name: 'architect-simulator-storage',
      version: 2, // Bumped to clear old cached inventory
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        // Don't persist inventory - always fetch fresh from API
      }),
      migrate: (persistedState, version) => {
        // Clear old inventory data from version 1
        if (version < 2) {
          return {
            ...persistedState,
            inventory: [],
            inventoryLoaded: false,
          };
        }
        return persistedState;
      },
    }
  )
);

export default useUserStore;
